import { default as NextLink } from 'next/link'

import { Link } from '@mui/material'
import { PortableText } from '@portabletext/react'

import { SanityBlockContentImage } from '@/components/SanityComponent/SanityBlockContentImage'
import { SanityBlockContentYoutube } from '@/components/SanityComponent/SanityBlockContentYoutube'
import { SanityCodeBlockContent } from '@/components/SanityComponent/SanityCodeBlockContent'
import { SanityCookieConsentLink } from '@/components/SanityComponent/SanityCookieConsentLink'
import { SanityParagraphLink } from '@/components/SanityComponent/SanityParagraphLink'
import { BlockContentTypography } from './BlockContentTypography'

import type { FC, ReactNode } from 'react'
import type { PortableTextComponents } from '@portabletext/react'
import type { BlockContent } from '@/gen/sanity/schema'

const components = (linkIcon?: ReactNode): PortableTextComponents => ({
  types: {
    extendedImage: (props) => {
      return <SanityBlockContentImage portableTextImageProps={props} />
    },
    youtube: (props) => {
      return <SanityBlockContentYoutube portableTextImageProps={props} />
    },
    cookieConsentRenewLink: (props) => {
      return <SanityCookieConsentLink portableTextImageProps={props} linkIcon={linkIcon} />
    },
    paragraphLink: (props) => {
      return <SanityParagraphLink portableTextImageProps={props} linkIcon={linkIcon} />
    },
    htmlBlock: (props) => {
      return <SanityCodeBlockContent htmlContent={props.value.html.code} />
    },
  },
  block: {
    h1: ({ value, children }) => (
      <BlockContentTypography mt={2} variant="h1" value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
    h2: ({ value, children }) => (
      <BlockContentTypography mt={2} variant="h2" value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
    h3: ({ value, children }) => (
      <BlockContentTypography mt={2} mb={2} variant="h3" value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
    h4: ({ value, children }) => (
      <BlockContentTypography mt={2} mb={2} variant="h4" value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
    normal: ({ value, children }) => (
      <BlockContentTypography variant="body1" mb={0.5} value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
  },
  marks: {
    link: ({ value, children }) => {
      const url = (value && value?.href) || '#'
      const target = url && url?.startsWith('http') ? '_blank' : '_self'

      return (
        <Link component={NextLink} href={url} target={target} rel="noreferrer">
          {children}
        </Link>
      )
    },

    small: ({ value, children }) => (
      <BlockContentTypography variant="body2" component="small" mb={0.5} value={{ value }}>
        {children}
      </BlockContentTypography>
    ),
  },
})

interface BlockContentConsumerProps {
  value: BlockContent
  linkIcon?: ReactNode
}

export const BlockContentConsumer: FC<BlockContentConsumerProps> = ({ value, linkIcon }) => {
  return <PortableText components={components(linkIcon)} value={value} />
}
